import { Q } from '@nozbe/watermelondb';
import { database, txn } from '@/db';
import { Customer } from '@/db/models/Customers';
import { Invoice, Payment } from '@/db/models/Billing';
import { derivePaymentStatus, toPaisa, toRupees } from '@/billing/billingEngine';

export type CustomerPaymentMethod = 'CASH' | 'UPI' | 'CARD' | 'BANK_TRANSFER' | 'CHEQUE';

export interface CustomerPaymentInput {
  customerId: string;
  amount: number;
  method: CustomerPaymentMethod;
  reference?: string;
  notes?: string;
}

export async function recordCustomerPayment(input: CustomerPaymentInput): Promise<{ ok: boolean; message?: string }> {
  const amount = Number(input.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return { ok: false, message: 'Enter a valid payment amount.' };
  }

  try {
    await txn(async (db) => {
      const customer = await db.get<Customer>('customers').find(input.customerId);
      const invoices = await db.get<Invoice>('invoices').query(
        Q.where('customer_id', input.customerId),
        Q.where('is_deleted', false),
        Q.where('type', 'SALE'),
        Q.sortBy('created_at', Q.asc),
      ).fetch();

      let remaining = amount;
      let appliedTotal = 0;
      const paymentCollection = db.get<Payment>('payments');
      for (const invoice of invoices) {
        if (remaining <= 0) break;
        const due = Math.max(0, invoice.dueAmount);
        if (due <= 0) continue;
        const applied = Math.min(due, remaining);
        remaining -= applied;
        appliedTotal += applied;

        await paymentCollection.create((record) => {
          record.invoiceId = invoice.id;
          record.method = input.method;
          record.amount = applied;
          record.transactionRef = input.reference?.trim() || null;
          record.status = 'COMPLETED';
          record.receivedAt = new Date();
          record.notes = input.notes?.trim() || 'Customer due collection';
          record.version = 1;
          record.isSynced = false;
          record.isDeleted = false;
        });

        await invoice.update((record) => {
          const paid = invoice.paidAmount + applied;
          const dueAfter = Math.max(0, invoice.totalAmount - paid);
          record.paidAmount = paid;
          record.dueAmount = dueAfter;
          record.paymentStatus = derivePaymentStatus(toPaisa(invoice.totalAmount), toPaisa(paid));
          record.version = invoice.version + 1;
          record.isSynced = false;
        });
      }

      const balanceReduction = Math.min(amount, Math.max(customer.balance, appliedTotal));
      await customer.update((record) => {
        record.balance = Math.max(0, customer.balance - balanceReduction);
        record.version = customer.version + 1;
        record.isSynced = false;
      });
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'Failed to record payment.' };
  }
}

export async function recomputeCustomerBalance(customerId: string): Promise<number> {
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('customer_id', customerId),
    Q.where('is_deleted', false),
    Q.where('type', 'SALE'),
  ).fetch();
  return toRupees(invoices.reduce((sum, invoice) => sum + toPaisa(Math.max(0, invoice.dueAmount)), 0));
}
